/**
 * @fileoverview Sync Status Display Component
 * 
 * Displays real-time sync status with progress indicators and error states
 * for knowledge base assignments.
 * 
 * Requirements: 4.6, 7.5, 7.6
 */

import React, { useMemo } from 'react';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  ArrowPathIcon,
  ClockIcon,
  WifiIcon,
  SignalSlashIcon
} from '@heroicons/react/24/outline';

/**
 * Sync status display component
 * 
 * @param {Object} props - Component props
 * @param {string} props.fileId - File ID to display status for
 * @param {string} props.parrotId - Parrot ID to display status for
 * @param {Object} props.status - Sync status object
 * @param {Object} props.connectionHealth - Connection health information
 * @param {boolean} props.showDetails - Whether to show detailed status
 * @param {Function} props.onRetry - Callback for retry action
 * @param {string} props.className - Additional CSS classes
 */
export function SyncStatusDisplay({
  fileId,
  parrotId,
  status,
  connectionHealth,
  showDetails = false,
  onRetry,
  className=''
}) {
  // Determine status display properties
  const statusInfo = useMemo(() => {
    if (!status) {
      return {
        type: 'unknown',
        label: 'Unknown',
        icon: ClockIcon,
        color: 'text-gray-500',
        bgColor: 'bg-gray-100',
        progress: 0
      };
    }

    switch (status.status) {
      case 'pending':
        return {
          type: 'pending',
          label: 'Pending',
          icon: ClockIcon,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100',
          progress: 0
        };
      
      case 'syncing':
        return {
          type: 'syncing',
          label: 'Syncing',
          icon: ArrowPathIcon,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          progress: status.progress || 0,
          animated: true
        };
      
      case 'completed':
        return {
          type: 'completed',
          label: 'Synced',
          icon: CheckCircleIcon,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          progress: 100
        };
      
      case 'failed':
        return {
          type: 'failed',
          label: 'Failed',
          icon: ExclamationTriangleIcon,
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          progress: 0
        };
      
      default:
        return {
          type: 'unknown',
          label: 'Unknown',
          icon: ClockIcon,
          color: 'text-gray-500',
          bgColor: 'bg-gray-100',
          progress: 0
        };
    }
  }, [status]);

  // Format last updated time
  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return null;
    
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) { // Less than 1 minute
      return 'Just now';
    } else if (diff < 3600000) { // Less than 1 hour
      const minutes = Math.floor(diff / 60000);
      return `${minutes}m ago`;
    } else if (diff < 86400000) { // Less than 1 day
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const StatusIcon = statusInfo.icon;

  return (
    <div className={`sync-status-display ${className}`}>
      {/* Main Status Indicator */}
      <div className="flex items-center space-x-2">
        <div className={`flex items-center justify-center w-6 h-6 rounded-full ${statusInfo.bgColor}`}>
          <StatusIcon 
            className={`w-4 h-4 ${statusInfo.color} ${statusInfo.animated ? 'animate-spin' : ''}`}
          />
        </div>
        
        <span className={`text-sm font-medium ${statusInfo.color}`}>
          {statusInfo.label}
        </span>

        {/* Progress Indicator */}
        {statusInfo.type === 'syncing' && statusInfo.progress > 0 && (
          <span className="text-xs text-gray-500">
            ({statusInfo.progress}%)
          </span>
        )}

        {/* Connection Status */}
        <div className="flex items-center">
          {connectionHealth?.connected ? (
            <WifiIcon className="w-4 h-4 text-green-500" title="Connected" />
          ) : connectionHealth?.usingPolling ? (
            <SignalSlashIcon className="w-4 h-4 text-yellow-500" title="Using polling fallback" />
          ) : (
            <SignalSlashIcon className="w-4 h-4 text-red-500" title="Disconnected" />
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {statusInfo.type === 'syncing' && statusInfo.progress > 0 && (
        <div className="mt-2">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${statusInfo.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Detailed Status */}
      {showDetails && (
        <div className="mt-3 space-y-2">
          {/* Error Message */}
          {status?.error && (
            <div className="flex items-start space-x-2 p-2 bg-red-50 rounded-md">
              <ExclamationTriangleIcon className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-red-700 font-medium">Error</p>
                <p className="text-xs text-red-600">{status.error}</p>
                {onRetry && (
                  <button
                    onClick={() => onRetry(fileId, parrotId)}
                    className="mt-1 text-xs text-red-600 hover:text-red-800 underline"
                  >
                    Retry sync
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Completion Details */}
          {status?.completedAt && (
            <div className="text-xs text-gray-500">
              Completed: {new Date(status.completedAt).toLocaleString()}
            </div>
          )}

          {/* Last Updated */}
          {status?.lastUpdated && (
            <div className="text-xs text-gray-500">
              Updated: {formatLastUpdated(status.lastUpdated)}
            </div>
          )}

          {/* Connection Health */}
          {connectionHealth && (
            <div className="text-xs text-gray-500">
              Connection: {connectionHealth.connected ? 'Live' : 'Polling'}
              {connectionHealth.lastHeartbeat && (
                <span className="ml-1">
                  (Last: {formatLastUpdated(connectionHealth.lastHeartbeat.getTime())})
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Bulk sync status display for multiple files/parrots
 * 
 * @param {Object} props - Component props
 * @param {Array} props.assignments - Array of assignment objects
 * @param {Object} props.syncStatuses - Map of sync statuses
 * @param {Object} props.connectionHealth - Connection health information
 * @param {Function} props.onRetry - Callback for retry action
 * @param {string} props.className - Additional CSS classes
 */
export function BulkSyncStatusDisplay({
  assignments = [],
  syncStatuses = {},
  connectionHealth,
  onRetry,
  className=''
}) {
  // Calculate overall status
  const overallStatus = useMemo(() => {
    if (assignments.length === 0) {
      return { type: 'empty', count: 0 };
    }

    const statusCounts = {
      pending: 0,
      syncing: 0,
      completed: 0,
      failed: 0,
      unknown: 0
    };

    assignments.forEach(assignment => {
      const key = `${assignment.fileId}:${assignment.parrotId}`;
      const status = syncStatuses[key];
      const statusType = status?.status || 'unknown';
      statusCounts[statusType] = (statusCounts[statusType] || 0) + 1;
    });

    return {
      type: 'summary',
      total: assignments.length,
      ...statusCounts
    };
  }, [assignments, syncStatuses]);

  if (overallStatus.type === 'empty') {
    return (
      <div className={`text-sm text-gray-500 ${className}`}>
        No assignments to sync
      </div>
    );
  }

  return (
    <div className={`bulk-sync-status ${className}`}>
      {/* Overall Status Summary */}
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-gray-900">
        <div className="flex items-center space-x-4">
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            Sync Status ({overallStatus.total} total)
          </div>
          
          {/* Status Counts */}
          <div className="flex items-center space-x-3 text-xs">
            {overallStatus.completed > 0 && (
              <span className="flex items-center space-x-1 text-green-600">
                <CheckCircleIcon className="w-3 h-3" />
                <span>{overallStatus.completed}</span>
              </span>
            )}
            
            {overallStatus.syncing > 0 && (
              <span className="flex items-center space-x-1 text-blue-600">
                <ArrowPathIcon className="w-3 h-3 animate-spin" />
                <span>{overallStatus.syncing}</span>
              </span>
            )}
            
            {overallStatus.pending > 0 && (
              <span className="flex items-center space-x-1 text-yellow-600">
                <ClockIcon className="w-3 h-3" />
                <span>{overallStatus.pending}</span>
              </span>
            )}
            
            {overallStatus.failed > 0 && (
              <span className="flex items-center space-x-1 text-red-600">
                <ExclamationTriangleIcon className="w-3 h-3" />
                <span>{overallStatus.failed}</span>
              </span>
            )}
          </div>
        </div>

        {/* Connection Indicator */}
        <div className="flex items-center space-x-2">
          {connectionHealth?.connected ? (
            <div className="flex items-center space-x-1 text-green-600">
              <WifiIcon className="w-4 h-4" />
              <span className="text-xs">Live</span>
            </div>
          ) : connectionHealth?.usingPolling ? (
            <div className="flex items-center space-x-1 text-yellow-600">
              <SignalSlashIcon className="w-4 h-4" />
              <span className="text-xs">Polling</span>
            </div>
          ) : (
            <div className="flex items-center space-x-1 text-red-600">
              <SignalSlashIcon className="w-4 h-4" />
              <span className="text-xs">Offline</span>
            </div>
          )}
        </div>
      </div>

      {/* Individual Assignment Statuses */}
      <div className="mt-3 space-y-2">
        {assignments.map(assignment => {
          const key = `${assignment.fileId}:${assignment.parrotId}`;
          const status = syncStatuses[key];
          
          return (
            <div key={key} className="flex items-center justify-between p-2 bg-white border rounded-md dark:bg-gray-800">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate dark:text-white">
                  {assignment.fileName || assignment.fileId}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  → {assignment.parrotName || assignment.parrotId}
                </div>
              </div>
              
              <div className="ml-3 flex-shrink-0">
                <SyncStatusDisplay
                  fileId={assignment.fileId}
                  parrotId={assignment.parrotId}
                  status={status}
                  connectionHealth={connectionHealth}
                  onRetry={onRetry}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SyncStatusDisplay;
